import { UserAgentApplication } from 'msal';
// LocalStorage replaced with native localStorage

const clientId = 'ba253099-8114-4eb0-b6e6-17cb8988fbd1';
const tenantId = '97e30b57-20e2-407e-980a-488e30b693e7';
const redirectUri = 'https://app.lk.customers.vdoc.dk/loginad';
const postLogoutRedirectUri = 'https://app.lk.customers.vdoc.dk/login'; //window.location.origin;

export const config = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri,
  },
};

const tokenConfig = {
  scopes: ['User.Read'],
};

export const auth = new UserAgentApplication(config);

auth.handleRedirectCallback((error) => {
  if (error) {
    console.error(error);
  }
});

export const isLoggedIn = auth.getAccount() !== null;

export const getToken = () =>
  auth
    .acquireTokenSilent(tokenConfig)
    .catch(() => auth.acquireTokenRedirect(tokenConfig))
    .then((authResponse) => {
      if (authResponse) return authResponse.idToken.rawIdToken;
    });

// Token management functions
export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem('JWT', accessToken);
  localStorage.setItem('REFRESH_TOKEN', refreshToken);
  // Reset refresh retry counter when new tokens are set
  refreshRetryCount = 0;
};

export const getAccessToken = (): string | null => {
  return localStorage.getItem('JWT');
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem('REFRESH_TOKEN');
};

export const clearTokens = () => {
  localStorage.removeItem('JWT');
  localStorage.removeItem('REFRESH_TOKEN');
  // Reset refresh retry counter when tokens are cleared
  refreshRetryCount = 0;
};

// Refresh token functionality
let isRefreshing = false;
let refreshRetryCount = 0;
const MAX_REFRESH_RETRIES = 3;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

// Export function to get current retry count (for debugging/monitoring)
export const getRefreshRetryCount = (): number => refreshRetryCount;

// Export function to reset retry count (for manual reset if needed)
export const resetRefreshRetryCount = (): void => {
  refreshRetryCount = 0;
};

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token!);
    }
  });

  failedQueue = [];
};

export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    refreshRetryCount = 0; // Reset counter when no refresh token
    throw new Error('No refresh token available');
  }

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  try {
    // Import axios here to avoid circular dependency
    const axios = (await import('axios')).default;
    const response = await axios.post(`${process.env.API}/auth/refresh`, {
      refresh_token: refreshToken,
    });

    const { access_token, refresh_token: newRefreshToken } = response.data;

    setTokens(access_token, newRefreshToken);
    processQueue(null, access_token);
    
    // Reset retry counter on successful refresh
    refreshRetryCount = 0;

    return access_token;
  } catch (error) {
    refreshRetryCount++;
    console.log(`Refresh token attempt ${refreshRetryCount}/${MAX_REFRESH_RETRIES} failed:`, error);

    if (refreshRetryCount >= MAX_REFRESH_RETRIES) {
      console.log('Max refresh token retries reached, forcing logout');
      refreshRetryCount = 0; // Reset counter
      processQueue(error, null);
      clearTokens();
      
      // Force redirect to login after max retries
      setTimeout(() => {
        window.location.href = '/login';
      }, 100);
      
      throw new Error('Max refresh token retries exceeded');
    }

    processQueue(error, null);
    clearTokens();
    throw error;
  } finally {
    isRefreshing = false;
  }
};

// Logout function that calls backend to revoke refresh token
export const logout = async (): Promise<void> => {
  try {
    const token = getAccessToken();
    if (token) {
      // Import axios here to avoid circular dependency
      const axios = (await import('axios')).default;

      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Logout timeout')), 3000));

      const logoutPromise = axios.post(
        `${process.env.API}/auth/logout`,
        {},
        {
          headers: { authorization: `Bearer ${token}` },
          timeout: 2000, // 2 second timeout
        }
      );

      await Promise.race([logoutPromise, timeoutPromise]);
    }
  } catch (error) {
    console.log('Backend logout call failed:', error);
  } finally {
    clearTokens();
    // Disconnect websocket on logout
    await disconnectWebSocket();
  }
};

// Function to disconnect websocket (imported dynamically to avoid circular dependency)
const disconnectWebSocket = async (): Promise<void> => {
  try {
    const { socket } = await import('src/boot/websocket');
    if (socket && socket.connected) {
      console.log('Disconnecting websocket on logout...');
      socket.disconnect();
    }
  } catch (error) {
    console.log('Failed to disconnect websocket:', error);
  }
};

// Simple logout function that only clears tokens (fallback)
export const logoutLocal = (): void => {
  clearTokens();
  // Attempt to disconnect websocket
  disconnectWebSocket().catch(console.error);
};

// Emergency logout function - absolute minimal implementation
export const emergencyLogout = (): void => {
  try {
    // Clear tokens
    localStorage.removeItem('JWT');
    localStorage.removeItem('REFRESH_TOKEN');

    // Clear local storage
    localStorage.removeItem('lagerstyring-store');

    // Reset retry counter
    refreshRetryCount = 0;

    // Force redirect
    window.location.href = '/login';
  } catch (error) {
    console.error('Emergency logout failed:', error);
    // Last resort
    window.location.reload();
  }
};

// Test function for refresh token retry logic (development/debugging only)
export const testRefreshTokenRetry = async (): Promise<void> => {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('testRefreshTokenRetry should only be used in development');
    return;
  }
  
  console.log('Testing refresh token retry logic...');
  console.log(`Current retry count: ${refreshRetryCount}`);
  
  // Simulate failed refresh attempts
  for (let i = 1; i <= MAX_REFRESH_RETRIES + 1; i++) {
    try {
      console.log(`Simulating refresh attempt ${i}...`);
      // This will fail and increment the counter
      await refreshAccessToken();
    } catch (error) {
      console.log(`Attempt ${i} failed as expected:`, error.message);
      console.log(`Current retry count: ${refreshRetryCount}`);
      
      if (error.message === 'Max refresh token retries exceeded') {
        console.log('Max retries reached - user should be redirected to login');
        break;
      }
    }
  }
};
